import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { PrincipalType } from '@app/models/principal';
import { UserItemToRead } from '@app/models/user-item-to-read';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { SecurityService } from '@app/services/security.service';
import { UserService } from '@app/services/user.service';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class UserItemsToReadService {

  constructor(
    private http: HttpClient,
    private securityService: SecurityService,
    private userService: UserService,
    private httpHelper: HttpHelper,
    private featureSettingsStore: FeatureSettingsStoreService) { }

  getAllUserItemsToRead(): Observable<Array<UserItemToRead>> {
    const httpParams = new HttpParams().append('includeDrafts', `${this.featureSettingsStore.state.includeDraftsInReqReadList}`);

    return this.http.get<Array<UserItemToRead>>(`${Constants.API_ENDPOINT}/me/useritemstoread`,
      {
        params: httpParams
      })
      .pipe(
        concatMap(result => {
          if (result && result.length > 0) {
            let groupIds = new Array<string>();
            const itemsToReadIds = new Array<string>(); // Used to add Ids for the user to prevent showing duplicates if user has been assigned both in group and explicit
            result.forEach(r => {
              const groupPrincipalIds = r.ItemToRead.ItemsToReadForPrincipal.filter(p => p.Principal.PrincipalType === PrincipalType.Group).map(p => p.PrincipalId);
              groupIds = groupIds.concat(groupPrincipalIds);
            });
            return forkJoin([this.securityService.checkOffice365GroupsMemberships(), this.userService.getMyDetailsFromMsGraph(), of(result)])
              .pipe(
                map(([groupMemberships, userDetails, itemsToRead]) => {
                  return itemsToRead.filter(i => {
                    const itemAlreadyReturned = itemsToReadIds.some(item => item === i.ItemToReadGuid); // used to check if that itemToRead has already been returned to be shown for user, so that no duplicates are shown
                    if (i.ItemToRead.ItemsToReadForPrincipal.some(p => p.Principal.PrincipalType === PrincipalType.User && p.PrincipalId === userDetails.id) && !itemAlreadyReturned) {
                      itemsToReadIds.push(i.ItemToReadGuid); // Add GUID to array when item has been returned for user, used to not return duplicates
                      return i;
                    }
                    const groupPrincipalIds = i.ItemToRead.ItemsToReadForPrincipal.filter(p => p.Principal.PrincipalType === PrincipalType.Group).map(p => p.PrincipalId);
                    const userIsMemberOfOneOfTheGroups = groupMemberships.some(g => groupPrincipalIds.some(p => p === g.GroupId && g.UserIsMember));
                    if (userIsMemberOfOneOfTheGroups && !itemAlreadyReturned) {
                      itemsToReadIds.push(i.ItemToReadGuid); // Add GUID to array when item has been returned for user, used to not return duplicates
                      return i;
                    }
                  });
                }));
          } else {
            return of(new Array<UserItemToRead>());
          }
        }),
        catchError(this.httpHelper.handleError({ operation: 'getAllUserItemsToRead', result: null, rethrow: true })));
  }

  getNewOrUpdatedUserToReadSinceLastVisit(lastVisitedDate: Date): Observable<Array<UserItemToRead>> {
    const httpParams = new HttpParams()
      .append('lastVisitedDate', `${lastVisitedDate.toISOString()}`)
      .append('includeDrafts', `${this.featureSettingsStore.state.includeDraftsInReqReadList}`);

    return this.http
      .get<Array<UserItemToRead>>(`${Constants.API_ENDPOINT}/me/useritemstoread/updates`, {
        params: httpParams
      })
      .pipe(
        concatMap(result => {
          if (result && result.length > 0) {
            let groupIds = new Array<string>();
            const itemsToReadIds = new Array<string>(); // Used to add Ids for the user to prevent showing duplicates if user has been assigned both in group and explicit
            result.forEach(r => {
              const groupPrincipalIds = r.ItemToRead.ItemsToReadForPrincipal.filter(p => p.Principal.PrincipalType === PrincipalType.Group).map(p => p.PrincipalId);
              groupIds = groupIds.concat(groupPrincipalIds);
            });
            return forkJoin([this.securityService.checkOffice365GroupsMemberships(), this.userService.getMyDetailsFromMsGraph(), of(result)])
              .pipe(
                map(([groupMemberships, userDetails, itemsToRead]) => {
                  return itemsToRead.filter(i => {
                    const itemAlreadyReturned = itemsToReadIds.some(item => item === i.ItemToReadGuid); // used to check if that itemToRead has already been returned to be shown for user, so that no duplicates are shown
                    if (i.ItemToRead.ItemsToReadForPrincipal.some(p => p.Principal.PrincipalType === PrincipalType.User && p.PrincipalId === userDetails.id) && !itemAlreadyReturned) {
                      itemsToReadIds.push(i.ItemToReadGuid); // Add GUID to array when item has been returned for user, used to not return duplicates
                      return i;
                    }
                    const groupPrincipalIds = i.ItemToRead.ItemsToReadForPrincipal.filter(p => p.Principal.PrincipalType === PrincipalType.Group).map(p => p.PrincipalId);
                    const userIsMemberOfOneOfTheGroups = groupMemberships.some(g => groupPrincipalIds.some(p => p === g.GroupId && g.UserIsMember));
                    if (userIsMemberOfOneOfTheGroups && !itemAlreadyReturned) {
                      itemsToReadIds.push(i.ItemToReadGuid); // Add GUID to array when item has been returned for user, used to not return duplicates
                      return i;
                    }
                  });
                }));
          } else {
            return of(new Array<UserItemToRead>());
          }
        }),
        catchError(this.httpHelper.handleError({ operation: 'getNewOrUpdatedUserToReadSinceLastVisit', result: null, rethrow: true })));
  }

  updateUserItem(guid: string, userItemToRead: UserItemToRead): Observable<UserItemToRead> {
    return this.http.put<UserItemToRead>(`${Constants.API_ENDPOINT}/me/useritemstoread?guid=${guid}`, userItemToRead)
      .pipe(
        catchError(this.httpHelper.handleError({ operation: 'updateUserItem', result: null, rethrow: true })));
  }

  getAllUserItemsHasRead(): Observable<Array<UserItemToRead>> {
    return this.http.get<Array<UserItemToRead>>(`${Constants.API_ENDPOINT}/me/useritemstoread/hasread`)
      .pipe(
        catchError(this.httpHelper.handleError({ operation: 'getAllUserItemsHasRead', result: null, rethrow: true })));
  }
}
